import { create } from 'zustand'

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'warning' | 'info'
  duration?: number
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean
  sidebarCollapsed: boolean

  // Theme
  darkMode: boolean

  // Modals
  activeModal: string | null
  modalData: Record<string, unknown>

  // Toasts
  toasts: Toast[]

  // Library
  libraryOpen: boolean

  // Transient: lets a feature request a full-width content area on a route that
  // is otherwise width-constrained (e.g. one stage inside a tab).
  contentFullWidth: boolean

  // Actions
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleDarkMode: () => void
  setDarkMode: (dark: boolean) => void
  openModal: (modalId: string, data?: Record<string, unknown>) => void
  closeModal: () => void
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  setLibraryOpen: (open: boolean) => void
  setContentFullWidth: (full: boolean) => void
}

const generateToastId = () => `toast-${Date.now()}`

export const useUIStore = create<UIState>((set) => ({
  sidebarOpen: true,
  sidebarCollapsed: false, // Start expanded by default on desktop
  darkMode: true, // Start in dark mode by default
  activeModal: null,
  modalData: {},
  toasts: [],
  libraryOpen: false,
  contentFullWidth: false,

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  toggleDarkMode: () => set((state) => {
    const newDarkMode = !state.darkMode
    // Toggle dark class on document
    if (newDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    return { darkMode: newDarkMode }
  }),

  setDarkMode: (dark) => set(() => {
    if (dark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    return { darkMode: dark }
  }),

  openModal: (modalId, data = {}) =>
    set({
      activeModal: modalId,
      modalData: data,
    }),

  closeModal: () =>
    set({
      activeModal: null,
      modalData: {},
    }),

  addToast: (toast) => {
    const id = generateToastId()
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }))

    // Auto-remove after duration
    const duration = toast.duration ?? 3000
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }))
      }, duration)
    }
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  setLibraryOpen: (open) => set({ libraryOpen: open }),

  setContentFullWidth: (full) => set({ contentFullWidth: full }),
}))

// Helper hook for showing toasts
export const showToast = (
  message: string,
  type: Toast['type'] = 'info',
  duration?: number
) => {
  useUIStore.getState().addToast({ message, type, duration })
}
